const normalized = (value) => String(value || '').trim().toLocaleLowerCase();

const DRAFT_VERSION = 2;
const valuesMatch = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const createEmployeeEditDraft = (serverData, draftData) => ({
  version: DRAFT_VERSION,
  serverData,
  draftData,
});

export const hasRestorableEmployeeEditDraft = (serverData, storedDraft) => {
  if (storedDraft?.version !== DRAFT_VERSION
      || !storedDraft.serverData || !storedDraft.draftData) return false;

  return Object.keys(storedDraft.draftData).some((key) => (
    !valuesMatch(storedDraft.draftData[key], storedDraft.serverData[key])
    && valuesMatch(serverData[key], storedDraft.serverData[key])
  ));
};

export const mergeEmployeeEditDraft = (serverData, storedDraft) => {
  // A legacy draft has no server baseline, so it is impossible to distinguish
  // an unsaved edit from stale data. Prefer the current server record safely.
  if (storedDraft?.version !== DRAFT_VERSION
      || !storedDraft.serverData || !storedDraft.draftData) return serverData;

  const baselineData = storedDraft.serverData;
  const draftData = Object.keys(storedDraft.draftData).reduce((result, key) => {
    const draftChanged = !valuesMatch(storedDraft.draftData[key], baselineData[key]);
    const serverUnchanged = valuesMatch(serverData[key], baselineData[key]);
    if (draftChanged && serverUnchanged) result[key] = storedDraft.draftData[key];
    return result;
  }, {});
  const merged = { ...serverData, ...draftData };
  const draftDepartmentMatchesServer = (
    merged.department_id && serverData.department_id
      ? String(merged.department_id) === String(serverData.department_id)
      : normalized(merged.department) === normalized(serverData.department)
  );

  if (!draftData.department_id && !normalized(draftData.department)
      && (serverData.department_id || normalized(serverData.department))) {
    merged.department_id = serverData.department_id || null;
    merged.department = serverData.department || '';
  }

  // Old browser drafts created before relational master data was introduced can
  // contain an empty designation. Do not let that stale empty value hide a valid
  // server assignment. If the draft intentionally changed Department, however,
  // retaining the previous Designation could create an invalid relationship.
  if (draftDepartmentMatchesServer
      && !draftData.designation_id && !normalized(draftData.designation)
      && (serverData.designation_id || normalized(serverData.designation))) {
    merged.designation_id = serverData.designation_id || null;
    merged.designation = serverData.designation || '';
  }

  // Stale browser drafts shouldn't wipe valid relational IDs
  if (!draftData.branch_id && serverData.branch_id) {
    merged.branch_id = serverData.branch_id;
  }
  if (!draftData.reporting_manager_id && serverData.reporting_manager_id) {
    merged.reporting_manager_id = serverData.reporting_manager_id;
  }
  if (String(draftData.department_id || '').startsWith('legacy:') && serverData.department_id) {
    merged.department_id = serverData.department_id;
  }
  if (draftDepartmentMatchesServer && String(draftData.designation_id || '').startsWith('legacy:') && serverData.designation_id) {
    merged.designation_id = serverData.designation_id;
  }

  return merged;
};

export const reconcileOrganizationSelection = (formData, departments, designations) => {
  let departmentId = formData.department_id || null;
  let designationId = formData.designation_id || null;
  let departmentName = formData.department || '';
  let designationName = formData.designation || '';

  let department = departments.find(
    (item) => String(item.id) === String(departmentId)
  );
  if (!department && departmentName) {
    const matches = departments.filter(
      (item) => normalized(item.name) === normalized(departmentName)
    );
    if (matches.length === 1) department = matches[0];
  }

  if (department) {
    departmentId = department.id;
    departmentName = department.name;
  }

  let designation = designations.find(
    (item) => String(item.id) === String(designationId)
      && (!departmentId || String(item.department) === String(departmentId))
  );
  if (!designation && designationName) {
    const matches = designations.filter(
      (item) => normalized(item.name) === normalized(designationName)
        && (!departmentId || String(item.department) === String(departmentId))
    );
    if (matches.length === 1) designation = matches[0];
  }

  if (designation) {
    designationId = designation.id;
    designationName = designation.name;
    if (!department) {
      department = departments.find(
        (item) => String(item.id) === String(designation.department)
      );
      if (department) {
        departmentId = department.id;
        departmentName = department.name;
      }
    }
  }

  const unchanged = String(formData.department_id || '') === String(departmentId || '')
    && String(formData.designation_id || '') === String(designationId || '')
    && formData.department === departmentName
    && formData.designation === designationName;

  return unchanged ? formData : {
    ...formData,
    department_id: departmentId,
    department: departmentName,
    designation_id: designationId,
    designation: designationName,
  };
};

export const organizationSelectionError = (formData) => {
  if (String(formData.department || '').trim() && !formData.department_id) {
    return `Department "${formData.department}" is not configured for this company. Create it in Master Data or select an existing Department.`;
  }
  if (String(formData.designation || '').trim() && !formData.designation_id) {
    return `Designation "${formData.designation}" is not configured for the selected Department. Create it in Master Data or select an existing Designation.`;
  }
  return '';
};
